import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import TransactionsTable from '@/features/user-profile/components/TransactionsTable';
import { SortTransaction, Transaction } from '@/services/transactions/TransactionTypes';
import Paginator from '@/components/Paginator/Paginator';
import { useEffect, useState } from 'react';
import useGetTransactions from '@/services/transactions/queries/useGetTransactions';
import { SortingState, Updater } from '@tanstack/react-table';
import useIsMobileView from '@/hooks/useIsMobileView';
import { twMerge } from 'tailwind-merge';

const LIMIT = 10;
const DEFAULT_SORT: SortTransaction = { field: 'createdAt', order: 'desc' };

const TransactionsTab = () => {
  const isMobileView = useIsMobileView();
  const [pageNumber, setPageNumber] = useState(0);
  const [sort, setSort] = useState<SortTransaction>(DEFAULT_SORT);
  const {
    data: transactionsData,
    isLoading,
    refetch,
  } = useGetTransactions({
    skip: pageNumber * LIMIT,
    take: LIMIT,
    sort,
  });
  const handlerSortingChange = (updaterOrValue: Updater<SortingState>) => {
    const applySorting = (currentSorting: SortingState): SortingState => {
      return typeof updaterOrValue === 'function' ? updaterOrValue(currentSorting) : updaterOrValue;
    };

    const newSorting = applySorting([]);

    if (Array.isArray(newSorting)) {
      newSorting.forEach(({ id, desc }) => {
        setSort({ field: id, order: desc ? 'desc' : 'asc' } as SortTransaction);
      });
    }
  };

  useEffect(() => {
    refetch();
  }, [pageNumber, sort]);

  const onPageNumberChange = (newValue: number) => setPageNumber(newValue - 1);

  return (
    <div className="flex min-h-[27.5em] w-full min-w-[min(950px,calc(100vw-50px))] max-w-[950px] flex-1 flex-col justify-start gap-5 text-[16px]">
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {transactionsData?.data && transactionsData?.data.totalCount > 0 ? (
            <TransactionsTable
              data={(transactionsData?.data.transactions as Transaction[]) || []}
              onSortingChange={handlerSortingChange}
              sorting={sort}
              containerClassName={twMerge(
                'h-auto min-h-[24em]  w-auto shrink overflow-auto scrollbar-phenom',
                isMobileView ? 'max-h-[calc(100dvh-228px)]' : 'max-h-[24em]',
              )}
            />
          ) : (
            <div className="flex h-full shrink-0 items-center justify-center py-2">
              <span className="text-white text-opacity-50">No Transactions yet</span>
            </div>
          )}
          {transactionsData?.data && transactionsData?.data.totalCount / LIMIT > 1 && (
            <Paginator
              currentPage={pageNumber + 1}
              itemsPerPage={LIMIT}
              onPageChange={onPageNumberChange}
              totalItems={transactionsData?.data.totalCount}
            />
          )}
        </>
      )}
    </div>
  );
};

export default TransactionsTab;
